/**
 * The character surface — what a game system contributes to the sheet and the
 * creation wizard.
 *
 * Before this existed, `CharacterCreationWizard` and `FullCharacterSheet` both
 * hardcoded the six 5e abilities. That is not merely a missing feature for
 * other systems: Doomsworn's ATTUNEMENT is the casting stat for Pacts and the
 * resistance stat in will-contests, so a sheet that cannot hold it makes every
 * Reckoning resolve at +0 for every player. A system whose numbers cannot be
 * entered is a system that does not run.
 *
 * So systems describe their surface and the UI renders the description. The
 * wizard asks the adapter which abilities exist and what values to hand out;
 * the sheet asks the adapter what else belongs on the page. Neither component
 * learns a system's name.
 *
 * Everything here is optional. A system that declares nothing gets the six
 * abilities from `getAbilities()` and the standard array, which is what 5e
 * wants anyway.
 */
// =============================================================================
// ABILITY KEYS
// =============================================================================
/**
 * The key an ability occupies on `Character.abilityScores`.
 *
 * Adapters identify abilities by short id (`str`, `atn`); the stored character
 * has always used the long lowercase name (`strength`). Rather than introduce a
 * second mapping table that can drift, the name IS the key — 5e's `Strength`
 * lands on `strength` exactly as every existing character already has it, and
 * Doomsworn's `Attunement` lands on `attunement`.
 */
export function abilityStorageKey(ability) {
    return ability.name.toLowerCase();
}
/** Storage keys for a whole ability list, in the system's own order. */
export function abilityStorageKeys(abilities) {
    return abilities.map(abilityStorageKey);
}
/** The six every d20 sheet has had since the beginning. */
export const BASELINE_ABILITY_KEYS = [
    'strength',
    'dexterity',
    'constitution',
    'intelligence',
    'wisdom',
    'charisma',
];
/** The classic 5e spread, and the default for any system that says nothing. */
export const STANDARD_ARRAY_5E = [15, 14, 13, 12, 10, 8];
/**
 * The creation spec for a system, falling back to its `getAbilities()` list
 * and the standard array.
 *
 * When a system has more abilities than the array has values, the array is
 * padded with the default score rather than truncating the ability list —
 * an ability with no value offered is still an ability the player can raise,
 * where a dropped ability is one they can never set at all.
 */
export function creationSpecFor(adapter) {
    const declared = adapter?.characterSurface?.getCreationSpec?.();
    if (declared)
        return declared;
    const abilities = adapter?.getAbilities() ?? [];
    const standardArray = [...STANDARD_ARRAY_5E];
    const defaultScore = 10;
    while (standardArray.length < abilities.length)
        standardArray.push(defaultScore);
    return {
        abilities,
        standardArray: standardArray.slice(0, Math.max(abilities.length, STANDARD_ARRAY_5E.length)),
        defaultScore,
        rolledPoolSize: Math.max(abilities.length, STANDARD_ARRAY_5E.length),
    };
}
/**
 * Sections a system contributes to the character sheet, or `[]` for a system
 * with nothing to add.
 */
export function sheetSectionsFor(adapter, character) {
    return adapter?.characterSurface?.getSheetSections?.(character) ?? [];
}
/** True when a section has anything worth drawing. */
export function sectionHasContent(section) {
    if (section.showWhenEmpty)
        return true;
    return section.fields.some(field => {
        switch (field.kind) {
            case 'text':
                return Boolean(field.text?.trim());
            case 'tags':
                return (field.tags?.length ?? 0) > 0;
            case 'entries':
                return (field.entries?.length ?? 0) > 0;
            default:
                return (field.value ?? 0) !== 0;
        }
    });
}
// =============================================================================
// SCORE TRANSLATION
// =============================================================================
/**
 * Translate stored scores (long keys — `strength`, `attunement`) into the short
 * ability ids the adapter and its rules engine use (`str`, `atn`).
 *
 * Only abilities the system declares are carried across, and only ones the
 * sheet actually has. An absent ability stays absent rather than arriving as 0
 * — for Doomsworn that distinction is load-bearing, since a missing Attunement
 * is a modifier of 0 while a score of 0 would be -5 applied to every creature
 * in the bestiary.
 */
export function toSystemAbilities(scores, abilities) {
    const out = {};
    if (!scores)
        return out;
    for (const ability of abilities) {
        const score = scores[abilityStorageKey(ability)];
        if (typeof score === 'number')
            out[ability.id] = score;
    }
    return out;
}
/** The reverse: short ids back onto the keys the character is stored under. */
export function fromSystemAbilities(abilities, definitions) {
    const out = {};
    if (!abilities)
        return out;
    for (const definition of definitions) {
        const score = abilities[definition.id];
        if (typeof score === 'number')
            out[abilityStorageKey(definition)] = score;
    }
    return out;
}
//# sourceMappingURL=system-sheet.js.map